import * as React from "react";
import { Flame, Award, Sparkles, Calendar } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { BlurReveal } from "@/components/effects/blur-reveal";
import { AmbientCanvas } from "@/components/effects/ambient-canvas";
import { CountUp } from "@/components/effects/count-up";
import { PageFrame } from "@/components/page-frame";
import { AppTopBar } from "@/components/app-topbar";
import { useAuth } from "@/lib/auth-context";
import { fetchLeaderboard } from "@/lib/queries";

export default function Streak() {
  const { user } = useAuth();
  const [streak, setStreak] = React.useState(0);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      const rows = await fetchLeaderboard(200);
      if (cancelled) return;
      const mine = rows.find(r => r.id === user?.id);
      setStreak(mine?.streak ?? 0);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [user?.id]);

  const today = new Date();
  const monthLabel = today.toLocaleDateString("en-GB", { month: "long", year: "numeric" });
  const todayIdx = today.getDate() - 1;
  const daysInMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0).getDate();

  return (
    <PageFrame surface="app">
      <AppTopBar />
      <div className="relative">
        <AmbientCanvas className="absolute inset-0 -z-10 w-full h-[800px] pointer-events-none" />
        <div className="relative mx-auto max-w-4xl px-4 md:px-6 pt-10 pb-32">
        <BlurReveal>
          <header className="rounded-3xl border border-line bg-bg-2 p-8 text-center relative overflow-hidden">
            <div aria-hidden className="absolute -top-12 left-1/2 -translate-x-1/2 w-80 h-80 rounded-full bg-accent/20 blur-3xl" />
            <div className="relative">
              <span className="inline-flex items-center justify-center w-20 h-20 rounded-3xl bg-accent/15 border border-accent-lo text-accent mb-5">
                <Flame className="w-10 h-10" />
              </span>
              <div className="text-xs uppercase tracking-widest text-fg-3 mb-2">Noor Streak</div>
              <div className="text-7xl md:text-8xl font-bold tracking-tight text-accent">
                {loading ? "—" : <CountUp to={streak} />}
              </div>
              <div className="text-fg-2">{streak === 1 ? "day lit" : "days lit in a row"}</div>
              <p className="mt-4 mx-auto max-w-md text-sm text-fg-2 leading-relaxed">
                One mode per day keeps your lantern alight. Miss a day and it resets — but your best streak is yours forever.
              </p>
            </div>
          </header>
        </BlurReveal>

        <div className="mt-8 grid md:grid-cols-3 gap-3">
          <BlurReveal>
            <Card>
              <CardContent>
                <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-fg-3 mb-1">
                  <Flame className="w-4 h-4 text-accent" /> Current
                </div>
                <div className="text-3xl font-bold tracking-tight">{streak} {streak === 1 ? "day" : "days"}</div>
              </CardContent>
            </Card>
          </BlurReveal>
          <BlurReveal>
            <Card>
              <CardContent>
                <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-fg-3 mb-1">
                  <Award className="w-4 h-4 text-accent" /> Today
                </div>
                <div className="text-3xl font-bold tracking-tight">{streak > 0 ? "Lit" : "Light it"}</div>
                <div className="text-xs text-fg-2 mt-1">Run any mode</div>
              </CardContent>
            </Card>
          </BlurReveal>
          <BlurReveal>
            <Card>
              <CardContent>
                <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-fg-3 mb-1">
                  <Sparkles className="w-4 h-4 text-accent" /> Multiplier
                </div>
                <div className="text-3xl font-bold tracking-tight">
                  {streak >= 7 ? "1.5×" : streak >= 3 ? "1.25×" : "1.0×"}
                </div>
                <div className="text-xs text-fg-2 mt-1">Streak bonus on points</div>
              </CardContent>
            </Card>
          </BlurReveal>
        </div>

        <BlurReveal delay={0.2}>
          <section className="mt-8 rounded-3xl border border-line bg-bg-2 p-6">
            <div className="flex items-center gap-2 mb-1">
              <Calendar className="w-4 h-4 text-accent" />
              <h2 className="text-sm font-semibold">This month</h2>
              <Badge variant="outline" className="ml-auto">{monthLabel}</Badge>
            </div>
            <p className="text-xs text-fg-3 mb-5">A lit square is a day you completed at least one mode this month.</p>
            <div className="grid grid-cols-7 gap-2 text-center">
              {Array.from({ length: daysInMonth }).map((_, i) => {
                const inStreak = i >= todayIdx - streak + 1 && i <= todayIdx;
                const today = i === todayIdx;
                return (
                  <div key={i}
                    className={
                      "aspect-square rounded-lg flex items-center justify-center text-[10px] " +
                      (inStreak ? "bg-accent/25 border border-accent-lo text-accent"
                        : today ? "bg-bg border border-accent text-accent"
                        : "bg-bg border border-line text-fg-3")
                    }>{i + 1}</div>
                );
              })}
            </div>
            <Separator className="my-6" />
            <p className="text-xs text-fg-3 text-center">
              <Sparkles className="inline w-3 h-3 text-accent" /> Streak history is computed from your study sessions.
            </p>
          </section>
        </BlurReveal>
        </div>
      </div>
    </PageFrame>
  );
}
